import { useState } from "react";
import { Button, Modal, Row, Col, Form } from "react-bootstrap";
import { Formik, FieldArray, Form as FormikForm, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

const ALLOWED_FILTER_KEYS = {
  panel: "Panel",
  // event_type: "Event Type",
  user_id: "User ID",
  user_role: "User Role",
  subject_id: "Subject ID",
  subject_role: "Subject Role",
};

const AdvancedFilterPopup = ({ onApply }) => {
  const [showPopup, setShowPopup] = useState(false);
  const [savedFilters, setSavedFilters] = useState([{ key: "", value: "" }]); // <-- persist filters

  const closePopup = () => setShowPopup(false);
  const openPopup = () => setShowPopup(true);

  const validationSchema = Yup.object().shape({
    filters: Yup.array()
      .of(
        Yup.object().shape({
          key: Yup.string().required("Please select a field."),
          value: Yup.string().trim().required("Please enter a value."),
        })
      )
      .test("unique-keys", "Each field can only be used once.", (filters) => {
        const keys = filters.map((f) => f.key).filter(Boolean);
        return keys.length === new Set(keys).size;
      }),
  });

  return (
    <>
      <Button onClick={openPopup} className="p-2">Advanced Filters</Button>

      <Modal
        show={showPopup}
        onHide={closePopup}
        backdrop="static"
        keyboard={false}
        centered
        size="md"
      >
        <Formik
          enableReinitialize
          initialValues={{ filters: savedFilters }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            const validFilters = values.filters.filter(
              (f) => f.key && f.value.trim() !== ""
            );
            setSavedFilters(validFilters.length ? validFilters : [{ key: "", value: "" }]); // save applied filters
            onApply(validFilters);
            closePopup();
          }}
        >
          {({ values, errors, resetForm }) => (
            <FormikForm>
              <Modal.Header closeButton>
                <Modal.Title>Advanced Filters</Modal.Title>
              </Modal.Header>

              <Modal.Body>
                <FieldArray name="filters">
                  {({ push, remove }) => (
                    <>
                      {values.filters.map((_, index) => (
                        <Row className="mb-3 align-items-start" key={index}>
                          <Col md={5}>
                            <Field as={Form.Select} name={`filters.${index}.key`}>
                              <option value="">Select Field</option>
                              {Object.entries(ALLOWED_FILTER_KEYS).map(([k, label]) => (
                                <option key={k} value={k}>
                                  {label}
                                </option>
                              ))}
                            </Field>
                            <ErrorMessage
                              name={`filters.${index}.key`}
                              component="div"
                              className="text-danger small mt-1"
                            />
                          </Col>

                          <Col md={5}>
                            <Field
                              as={Form.Control}
                              type="text"
                              placeholder="Enter value"
                              name={`filters.${index}.value`}
                            />
                            <ErrorMessage
                              name={`filters.${index}.value`}
                              component="div"
                              className="text-danger small mt-1"
                            />
                          </Col>

                          <Col md={2} className="text-end">
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => remove(index)}
                              className="px-2"
                              disabled={values.filters.length === 1}
                            >
                              ✕
                            </Button>
                          </Col>
                        </Row>
                      ))}
                      <Button
                        variant="secondary"
                        onClick={() => push({ key: "", value: "" })}
                      >
                        + Add Filter
                      </Button>

                      {typeof errors.filters === "string" && (
                        <div className="text-danger small mt-2">{errors.filters}</div>
                      )}
                    </>
                  )}
                </FieldArray>
              </Modal.Body>

              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={() => {
                    resetForm();
                    setSavedFilters([{ key: "", value: "" }]); // clear saved filters
                    onApply([]); // fetch fresh data
                    closePopup();
                  }}
                >
                  Reset
                </Button>
                <Button variant="primary" type="submit">
                  Apply
                </Button>
              </Modal.Footer>
            </FormikForm>
          )}
        </Formik>
      </Modal>
    </>
  );
};

export default AdvancedFilterPopup;
